import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Conversation, ConversationParticipantOption, ConversationParticipantRef, Message } from '../types/Conversation';
import { getCustomListIdFromNavSection, NavSection } from '../types/conversationNav';
import { isDirectConversation, isExternalConversation, isRegularChannelConversation, isRegularDirectConversation, isWhatsAppGroupConversation } from '../utils/conversationClassification';
import { parseMessageContentWithEmbeddedMedia } from '../utils/embeddedMediaFromText';
import { formatInteractiveSelectionReply, getInteractiveMessageSummary, isMediaTypePlaceholder, isVoiceRecordingName, stripInteractiveSelectionPayloadSuffix } from '../utils/messageUtils';
import { maybeMaskChannelIdentitiesInText } from '../../contacts/contactChannelUtils';
import { stripWhatsAppLidFromName } from '../utils/whatsappIdentity';
import detailsStyles from '../components/ConversationDetails.module.css';

const captionPlaceholderPattern = /^\[(image|video|document|audio message|sticker|location)\]$/i;

const getLocationCoordinatesLabel = (message: Message): string | null => {
  if (message.locationLatitude == null || message.locationLongitude == null) {
    return null;
  }

  return `${message.locationLatitude.toFixed(6)}, ${message.locationLongitude.toFixed(6)}`;
};

const getLocationHref = (message: Message): string | null => {
  if (message.locationUrl?.trim()) {
    return message.locationUrl;
  }

  const coordinates = getLocationCoordinatesLabel(message);
  if (!coordinates) {
    return null;
  }

  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(coordinates)}`;
};

interface KnowledgeCitation {
  fileName: string;
  chunkIndex: number;
}

const knowledgeCitationPattern = /\[([^\]\r\n#]{1,180})#(\d{1,5})\]/g;

const extractKnowledgeCitations = (text: string): KnowledgeCitation[] => {
  const results: KnowledgeCitation[] = [];
  const seen = new Set<string>();
  const regex = new RegExp(knowledgeCitationPattern.source, 'g');
  let match: RegExpExecArray | null = regex.exec(text);

  while (match) {
    const fileName = (match[1] || '').trim();
    const chunkIndexRaw = Number.parseInt(match[2] || '', 10);
    if (!fileName || !Number.isFinite(chunkIndexRaw)) {
      match = regex.exec(text);
      continue;
    }

    const chunkIndex = Math.max(1, chunkIndexRaw);
    const key = `${fileName.toLowerCase()}#${chunkIndex}`;
    if (seen.has(key)) {
      match = regex.exec(text);
      continue;
    }

    seen.add(key);
    results.push({ fileName, chunkIndex });
    match = regex.exec(text);
  }

  return results;
};

const removeKnowledgeCitationTokens = (text: string): string => {
  return text
    .replace(knowledgeCitationPattern, '')
    .replace(/\s+,/g, ',')
    .replace(/,{2,}/g, ',')
    .replace(/,\s*(\n|$)/g, '$1')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/\s{2,}/g, ' ')
    .trim();
};

const renderMentionText = (
  message: Message,
  text: string,
  participantOptionById: Map<string, ConversationParticipantOption>,
  onMentionClick: (payload: {
    participantId: string;
    participantType: ConversationParticipantRef['type'];
    participantName: string;
    email?: string;
    avatarColor?: string;
  }) => void
): ReactNode => {
  if (!text) {
    return text;
  }

  const mentions = (message.mentions || [])
    .filter(mention => Number.isFinite(mention.position) && Number.isFinite(mention.length) && mention.length > 0)
    .sort((left, right) => left.position - right.position);

  if (mentions.length === 0) {
    return text;
  }

  const chunks: ReactNode[] = [];
  let cursor = 0;

  for (let index = 0; index < mentions.length; index += 1) {
    const mention = mentions[index];
    const start = Math.max(0, Math.min(text.length, mention.position));
    const end = Math.max(start, Math.min(text.length, mention.position + mention.length));

    if (start < cursor || end <= start) {
      continue;
    }

    if (start > cursor) {
      chunks.push(text.slice(cursor, start));
    }

    const tokenText = text.slice(start, end) || mention.mentionText || `@${mention.mentionedParticipantName}`;
    chunks.push(
      <button
        key={`mention-${start}-${index}`}
        type="button"
        className={`${detailsStyles.mentionToken} ${detailsStyles.mentionTokenButton}`}
        title={mention.mentionedParticipantName}
        onClick={() => {
          const participant = participantOptionById.get(mention.mentionedParticipantId);
          onMentionClick({
            participantId: mention.mentionedParticipantId,
            participantType: mention.mentionedParticipantType,
            participantName: mention.mentionedParticipantName,
            email: participant?.email,
            avatarColor: participant?.avatarColor,
          });
        }}
      >
        {tokenText}
      </button>
    );

    cursor = end;
  }

  if (cursor < text.length) {
    chunks.push(text.slice(cursor));
  }

  if (chunks.length === 0) {
    return text;
  }

  return <>{chunks}</>;
};

interface UseConversationPresentationOptions {
  list: Conversation[];
  hiddenList: Conversation[];
  customListConversations: Conversation[];
  navSection: NavSection;
  participantOptions: ConversationParticipantOption[];
  enableInteractiveSelection?: boolean;
  currentViewerParticipant?: ConversationParticipantRef;
  currentEmployeeId?: string;
  transcribingMessageId?: string | null;
  onTranscribeAudio?: (message: Message) => void | Promise<void>;
  translatingMessageId?: string | null;
  translatedMessagesById?: Record<string, string>;
  translatedMessageVisibilityById?: Record<string, boolean>;
  onTranslateMessage?: (message: Message) => void | Promise<void>;
  onToggleTranslatedMessageVisibility?: (messageId: string) => void;
  onReplyToInteractiveSelection?: (message: Message, replyText: string) => void | Promise<void>;
  onStartDirectMessage?: (participant: {
    participantId: string;
    participantType: ConversationParticipantRef['type'];
    participantName: string;
  }) => Promise<void> | void;
  maskChannelValues?: boolean;
}

interface UseConversationPresentationResult {
  currentConversationList: Conversation[];
  isExternalConversation: (conversation: Conversation) => boolean;
  isRegularDirectConversation: (conversation: Conversation) => boolean;
  isRegularChannelConversation: (conversation: Conversation) => boolean;
  getParticipantDisplayName: (participant: ConversationParticipantRef) => string;
  getConversationDisplayName: (conversation: Conversation) => string;
  renderMessageContent: (message: Message) => React.ReactNode;
  mentionDetailsPopup: React.ReactNode;
  imageViewerPopup: React.ReactNode;
}

export { isAiAssistantDirectConversation, isEmployeeAiAssistantDirectConversation, isChannelConversation, isDirectConversation, isExternalConversation, isRegularChannelConversation, isRegularDirectConversation } from '../utils/conversationClassification';

export const useConversationPresentation = ({
  list,
  hiddenList,
  customListConversations,
  navSection,
  participantOptions,
  enableInteractiveSelection = true,
  currentViewerParticipant,
  currentEmployeeId,
  transcribingMessageId,
  onTranscribeAudio,
  translatingMessageId,
  translatedMessagesById,
  translatedMessageVisibilityById,
  onTranslateMessage,
  onToggleTranslatedMessageVisibility,
  onReplyToInteractiveSelection,
  onStartDirectMessage,
  maskChannelValues = false,
}: UseConversationPresentationOptions): UseConversationPresentationResult => {
  const { t } = useTranslation('conversations');
  const maskChannelIdentities = Boolean(maskChannelValues);
  const [activeMention, setActiveMention] = useState<{
    participantId: string;
    participantType: ConversationParticipantRef['type'];
    participantName: string;
    email?: string;
    avatarColor?: string;
  } | null>(null);
  const [maximizedMedia, setMaximizedMedia] = useState<{ type: 'image' | 'video'; src: string; alt?: string } | null>(null);
  const participantOptionById = useMemo(() => {
    return new Map(participantOptions.map(option => [option.id, option]));
  }, [participantOptions]);

  useEffect(() => {
    if (!maximizedMedia) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMaximizedMedia(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [maximizedMedia]);

  const mentionDetailsPopup = activeMention ? (
    <div
      className={detailsStyles.modalOverlay}
      role="dialog"
      aria-modal="true"
      aria-label={t('mentionDetailsTitle')}
      onClick={() => setActiveMention(null)}
    >
      <div className={`${detailsStyles.modalCard} ${detailsStyles.mentionDetailsCard}`} onClick={event => event.stopPropagation()}>
        <div className={`${detailsStyles.modalHeader} ${detailsStyles.mentionDetailsHeader}`}>
          <div className={detailsStyles.modalTitle}>{t('mentionDetailsTitle')}</div>
          <button type="button" className={detailsStyles.modalCloseBtn} onClick={() => setActiveMention(null)}>
            {t('close', { ns: 'common' })}
          </button>
        </div>
        <div className={detailsStyles.mentionDetailsBody}>
          <div className={detailsStyles.mentionIdentityRow}>
            <span
              className={detailsStyles.mentionIdentityAvatar}
              style={activeMention.avatarColor ? { backgroundColor: activeMention.avatarColor } : undefined}
              aria-hidden="true"
            >
              {activeMention.participantName.trim().charAt(0).toUpperCase() || '@'}
            </span>
            <div className={detailsStyles.mentionIdentityText}>
              <div className={detailsStyles.mentionIdentityName}>{activeMention.participantName}</div>
              {activeMention.email ? <div className={detailsStyles.mentionIdentityEmail}>{activeMention.email}</div> : <div className={detailsStyles.mentionIdentityEmail}>{t('participant')}</div>}
            </div>
          </div>
        </div>
        {onStartDirectMessage && activeMention.participantType !== 'system' && activeMention.participantType !== 'businessSuite' ? (
          <div className={`${detailsStyles.modalActions} ${detailsStyles.mentionDetailsActions}`}>
            <button
              type="button"
              className={detailsStyles.modalPrimaryBtn}
              onClick={() => {
                void onStartDirectMessage({
                  participantId: activeMention.participantId,
                  participantType: activeMention.participantType,
                  participantName: activeMention.participantName,
                });
                setActiveMention(null);
              }}
            >
              <svg className={detailsStyles.mentionActionIcon} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M4 12h12M12 6l8 6-8 6" />
              </svg>
              <span>{t('newDirectMessage')}</span>
            </button>
          </div>
        ) : null}
      </div>
    </div>
  ) : null;

  const imageViewerPopup = maximizedMedia ? (
    <div
      className={detailsStyles.imageViewerOverlay}
      role="dialog"
      aria-modal="true"
      aria-label={maximizedMedia.type === 'image' ? t('imageMessage') : t('openVideo')}
      onClick={() => setMaximizedMedia(null)}
    >
      <button
        type="button"
        className={detailsStyles.imageViewerCloseButton}
        onClick={() => setMaximizedMedia(null)}
      >
        {t('close', { ns: 'common' })}
      </button>
      {maximizedMedia.type === 'image' ? (
        <img
          className={detailsStyles.imageViewerImage}
          src={maximizedMedia.src}
          alt={maximizedMedia.alt || t('imageMessage')}
          onClick={event => event.stopPropagation()}
        />
      ) : (
        <video
          className={detailsStyles.imageViewerVideo}
          src={maximizedMedia.src}
          controls
          autoPlay
          onClick={event => event.stopPropagation()}
        />
      )}
    </div>
  ) : null;

  const getParticipantDisplayName = useCallback((participant: ConversationParticipantRef) => {
    if (participant.type === 'system') {
      return t('system');
    }

    if (participant.type === 'aiAssistant') {
      return t('aiAssistant');
    }

    const explicitName = stripWhatsAppLidFromName(participant.name);
    if (participant.type === 'businessSuite') {
      return explicitName || t('businessSuite', { ns: 'conversations', defaultValue: 'Business Suite' });
    }

    if (explicitName) {
      return maybeMaskChannelIdentitiesInText(explicitName, maskChannelIdentities);
    }

    const participantOption = participantOptionById.get(participant.id);
    const optionName = stripWhatsAppLidFromName(participantOption?.name);
    if (optionName) {
      return maybeMaskChannelIdentitiesInText(optionName, maskChannelIdentities);
    }

    if (participant.type === 'companyMember') {
      if (currentEmployeeId && participant.id === currentEmployeeId) {
        return t('you', { ns: 'common' });
      }

      return t('companyMember');
    }

    if (participant.type === 'lead') {
      return t('lead');
    }

    return t('customer');
  }, [currentEmployeeId, maskChannelIdentities, participantOptionById, t]);

  const currentConversationList = useMemo(() => {
    if (getCustomListIdFromNavSection(navSection)) {
      return customListConversations;
    }

    return navSection === 'spam' ? hiddenList : list;
  }, [customListConversations, hiddenList, list, navSection]);

  const getConversationDisplayName = useCallback((conversation: Conversation) => {
    const explicitTitle = stripWhatsAppLidFromName(conversation.title);
    const isPlaceholderWhatsAppGroupTitle = /^(whatsapp group|whatsapp qrupu|whatsapp grubu)$/i.test(explicitTitle);

    if (isWhatsAppGroupConversation(conversation)) {
      if (explicitTitle && !isPlaceholderWhatsAppGroupTitle) {
        return maybeMaskChannelIdentitiesInText(explicitTitle, maskChannelIdentities);
      }

      return t('whatsAppGroup', { ns: 'conversations', defaultValue: 'WhatsApp group' });
    }

    if (explicitTitle) {
      return maybeMaskChannelIdentitiesInText(explicitTitle, maskChannelIdentities);
    }

    if (isDirectConversation(conversation) && conversation.participants.length > 0) {
      const otherParticipants = currentEmployeeId
        ? conversation.participants.filter(participant => !(participant.type === 'companyMember' && participant.id === currentEmployeeId))
        : conversation.participants;
      const externalParticipant = otherParticipants.find(participant => participant.type === 'customer' || participant.type === 'lead');
      const otherParticipant = externalParticipant
        ?? otherParticipants.find(participant => participant.type === 'companyMember')
        ?? otherParticipants.find(participant => participant.type === 'aiAssistant')
        ?? otherParticipants[0];

      if (otherParticipant) {
        const otherParticipantName = getParticipantDisplayName(otherParticipant).trim();
        if (otherParticipantName) {
          return otherParticipantName;
        }
      }
    }

    const participantNames = conversation.participants
      .map(participant => getParticipantDisplayName(participant))
      .filter(Boolean);

    if (participantNames.length > 0) {
      return Array.from(new Set(participantNames)).join(', ');
    }

    return t('conversation');
  }, [currentEmployeeId, getParticipantDisplayName, maskChannelIdentities, t]);

  const renderMessageContent = useCallback((message: Message) => {
    const caption = message.content?.trim() ?? '';
    const hasMeaningfulCaption = caption.length > 0 && !captionPlaceholderPattern.test(caption) && !isMediaTypePlaceholder(caption) && !isVoiceRecordingName(caption);
    const transcript = message.transcription?.trim() ?? '';
    const hasTranscript = transcript.length > 0;
    const isTranscribing = transcribingMessageId === message.id;
    const locationTitle = message.locationName?.trim() || t('sharedLocation');
    const locationAddress = message.locationAddress?.trim() ?? '';
    const locationCoordinates = getLocationCoordinatesLabel(message);
    const locationHref = getLocationHref(message);
    const interactiveSummary = getInteractiveMessageSummary(message);
    const translatedText = translatedMessagesById?.[message.id]?.trim() ?? '';
    const hasTranslatedText = translatedText.length > 0;
    const isTranslatedVisible = translatedMessageVisibilityById?.[message.id] ?? true;
    const knowledgeCitations = message.messageType === 'text' && message.sender.type === 'aiAssistant'
      ? extractKnowledgeCitations(message.content || '')
      : [];
    const textWithoutKnowledgeCitations = knowledgeCitations.length > 0
      ? removeKnowledgeCitationTokens(message.content || '')
      : message.content;

    if (message.messageType === 'image' && message.mediaUrl) {
      const imageUrl = message.mediaUrl;
      const imageAlt = message.fileName || t('imageMessage');
      return (
        <>
          <button
            type="button"
            className={detailsStyles.mediaImageButton}
            onClick={() => setMaximizedMedia({ type: 'image', src: imageUrl, alt: imageAlt })}
            title={t('openInFullView')}
          >
            <img className={detailsStyles.mediaImage} src={imageUrl} alt={imageAlt} loading="lazy" />
          </button>
          {hasMeaningfulCaption && <div className={detailsStyles.mediaCaption}>{renderMentionText(message, message.content, participantOptionById, setActiveMention)}</div>}
        </>
      );
    }

    if (message.messageType === 'audio' && message.mediaUrl) {
      return (
        <>
          <div className={detailsStyles.audioMessageCard}>
            <div className={detailsStyles.audioMessageHeader}>
              <div className={detailsStyles.audioWaveform} aria-hidden="true">
                <span />
                <span />
                <span />
                <span />
                <span />
                <span />
              </div>
              <div className={detailsStyles.audioMessageMeta}>
                <span className={detailsStyles.audioMessageLabel}>{t('audioMessage')}</span>
              </div>
            </div>
            <audio className={detailsStyles.mediaPlayer} controls preload="metadata" src={message.mediaUrl}>
              <a className={detailsStyles.mediaLink} href={message.mediaUrl} target="_blank" rel="noreferrer">{t('openAudio')}</a>
            </audio>
            {hasTranscript ? (
              <div className={detailsStyles.audioTranscriptCard}>
                <div className={detailsStyles.audioTranscriptHeader}>
                  <span className={detailsStyles.audioTranscriptLabel}>{t('transcript')}</span>
                  {message.transcriptionUpdatedAt ? (
                    <span className={detailsStyles.audioTranscriptTimestamp}>
                      {new Date(message.transcriptionUpdatedAt).toLocaleString()}
                    </span>
                  ) : null}
                </div>
                <div className={detailsStyles.audioTranscriptText}>{transcript}</div>
              </div>
            ) : onTranscribeAudio ? (
              <button
                type="button"
                className={detailsStyles.audioTranscriptAction}
                onClick={() => void onTranscribeAudio(message)}
                disabled={isTranscribing}
              >
                {isTranscribing ? t('transcribing') : t('transcribeAudio')}
              </button>
            ) : null}
          </div>
          {hasMeaningfulCaption && <div className={detailsStyles.mediaCaption}>{renderMentionText(message, message.content, participantOptionById, setActiveMention)}</div>}
        </>
      );
    }

    if (message.messageType === 'video' && message.mediaUrl) {
      const videoUrl = message.mediaUrl;
      return (
        <>
          <video
            className={detailsStyles.mediaPlayer}
            controls
            preload="metadata"
            src={videoUrl}
            onDoubleClick={() => setMaximizedMedia({ type: 'video', src: videoUrl })}
            title={t('openInFullView')}
          >
            <a className={detailsStyles.mediaLink} href={videoUrl} target="_blank" rel="noreferrer">{t('openVideo')}</a>
          </video>
          {hasMeaningfulCaption && <div className={detailsStyles.mediaCaption}>{renderMentionText(message, message.content, participantOptionById, setActiveMention)}</div>}
        </>
      );
    }

    if (message.messageType === 'document' && message.mediaUrl) {
      return (
        <>
          <a className={detailsStyles.mediaLink} href={message.mediaUrl} target="_blank" rel="noreferrer">
            {message.fileName || t('openDocument')}
          </a>
          {hasMeaningfulCaption && <div className={detailsStyles.mediaCaption}>{renderMentionText(message, message.content, participantOptionById, setActiveMention)}</div>}
        </>
      );
    }

    if (message.messageType === 'sticker' && message.mediaUrl) {
      return (
        <>
          <img className={detailsStyles.stickerImage} src={message.mediaUrl} alt={message.fileName || t('stickerMessage')} loading="lazy" />
          {hasMeaningfulCaption && <div className={detailsStyles.mediaCaption}>{renderMentionText(message, message.content, participantOptionById, setActiveMention)}</div>}
        </>
      );
    }

    if (message.messageType === 'location') {
      return (
        <>
          <div className={detailsStyles.locationCard}>
            <span className={detailsStyles.locationEyebrow}>{t('locationMessage')}</span>
            <strong className={detailsStyles.locationTitle}>{locationTitle}</strong>
            {locationAddress ? <span className={detailsStyles.locationMeta}>{locationAddress}</span> : null}
            {locationCoordinates ? <span className={detailsStyles.locationMeta}>{locationCoordinates}</span> : null}
            {locationHref ? (
              <a className={detailsStyles.locationLink} href={locationHref} target="_blank" rel="noreferrer">
                {t('openLocation')}
              </a>
            ) : null}
          </div>
          {hasMeaningfulCaption && <div className={detailsStyles.mediaCaption}>{renderMentionText(message, message.content, participantOptionById, setActiveMention)}</div>}
        </>
      );
    }

    if (message.messageType === 'interactive') {
      const viewerParticipant = currentViewerParticipant;
      const isInteractiveMessageSender = viewerParticipant
        ? message.sender.type === viewerParticipant.type && message.sender.id === viewerParticipant.id
        : false;
      const canReplyToInteractiveSelection = enableInteractiveSelection
        && Boolean(onReplyToInteractiveSelection)
        && Boolean(viewerParticipant)
        && !isInteractiveMessageSender;
      const inboundSelectionText = !message.interactiveList?.sections?.length
        && !message.interactiveButtons?.buttons?.length
        && hasMeaningfulCaption
        ? stripInteractiveSelectionPayloadSuffix(caption)
        : null;

      return (
        <div className={detailsStyles.interactiveCard}>
          <span className={detailsStyles.interactiveEyebrow}>{t('interactiveMessage')}</span>
          {interactiveSummary?.bodyText ? <strong className={detailsStyles.interactiveBody}>{interactiveSummary.bodyText}</strong> : null}
          {!interactiveSummary?.bodyText && inboundSelectionText ? (
            <strong className={detailsStyles.interactiveBody}>{renderMentionText(message, inboundSelectionText, participantOptionById, setActiveMention)}</strong>
          ) : null}
          <div className={detailsStyles.interactiveMetaRow}>
            {interactiveSummary?.buttonText ? <span className={detailsStyles.interactiveMetaChip}>{t('interactiveButtonValue', { value: interactiveSummary.buttonText })}</span> : null}
            {interactiveSummary?.optionCount ? <span className={detailsStyles.interactiveMetaChip}>{t('interactiveOptionsValue', { count: interactiveSummary.optionCount })}</span> : null}
            {interactiveSummary?.buttonCount ? <span className={detailsStyles.interactiveMetaChip}>{t('interactiveButtonsValue', { count: interactiveSummary.buttonCount })}</span> : null}
          </div>
          {message.interactiveList?.sections?.length ? (
            <div className={detailsStyles.interactiveSectionsList}>
              {message.interactiveList.sections.map((section, sectionIndex) => (
                <div key={`interactive-display-section-${sectionIndex}`} className={detailsStyles.interactiveSectionCard}>
                  {section.title ? <span className={detailsStyles.interactiveSectionTitle}>{section.title}</span> : null}
                  {section.rows.map(row => (
                    canReplyToInteractiveSelection ? (
                      <button
                        key={row.id}
                        type="button"
                        className={`${detailsStyles.interactiveRowCard} ${detailsStyles.interactiveRowAction}`}
                        onClick={() => void onReplyToInteractiveSelection?.(message, formatInteractiveSelectionReply(row))}
                      >
                        <strong>{row.title}</strong>
                        {row.description ? <span>{row.description}</span> : null}
                      </button>
                    ) : (
                      <div key={row.id} className={detailsStyles.interactiveRowCard}>
                        <strong>{row.title}</strong>
                        {row.description ? <span>{row.description}</span> : null}
                      </div>
                    )
                  ))}
                </div>
              ))}
            </div>
          ) : message.interactiveButtons?.buttons?.length ? (
            <div className={detailsStyles.interactiveSectionsList}>
              <div className={detailsStyles.interactiveSectionCard}>
                {message.interactiveButtons.buttons.map(button => (
                  canReplyToInteractiveSelection ? (
                    <button
                      key={button.id}
                      type="button"
                      className={`${detailsStyles.interactiveRowCard} ${detailsStyles.interactiveRowAction}`}
                      onClick={() => void onReplyToInteractiveSelection?.(message, button.title)}
                    >
                      <strong>{button.title}</strong>
                    </button>
                  ) : (
                    <div key={button.id} className={detailsStyles.interactiveRowCard}>
                      <strong>{button.title}</strong>
                    </div>
                  )
                ))}
              </div>
            </div>
          ) : null}
        </div>
      );
    }

    const sourceText = textWithoutKnowledgeCitations || '';
    const contentSegments = parseMessageContentWithEmbeddedMedia(sourceText);
    const hasInlineMedia = contentSegments.some(segment => segment.type === 'image' || segment.type === 'video');
    const textContent = hasInlineMedia
      ? contentSegments.map((segment, index) => {
          if (segment.type === 'text') {
            return <div key={`text-${index}`}>{segment.value}</div>;
          }

          if (segment.type === 'video') {
            return (
              <video
                key={`video-${index}`}
                className={detailsStyles.mediaPlayer}
                controls
                preload="metadata"
                src={segment.url}
                onDoubleClick={() => setMaximizedMedia({ type: 'video', src: segment.url })}
                title={t('openInFullView')}
              >
                <a className={detailsStyles.mediaLink} href={segment.url} target="_blank" rel="noreferrer">{t('openVideo')}</a>
              </video>
            );
          }

          const imageAlt = segment.alt || t('imageMessage');
          return (
            <button
              key={`image-${index}`}
              type="button"
              className={detailsStyles.mediaImageButton}
              onClick={() => setMaximizedMedia({ type: 'image', src: segment.url, alt: imageAlt })}
              title={t('openInFullView')}
            >
              <img className={detailsStyles.mediaImage} src={segment.url} alt={imageAlt} loading="lazy" />
            </button>
          );
        })
      : renderMentionText(message, sourceText, participantOptionById, setActiveMention);

    const translationBlock = hasTranslatedText && isTranslatedVisible ? (
      <div className={detailsStyles.messageTranslationBlock}>
        <div className={detailsStyles.messageTranslationCard}>
          <span className={detailsStyles.messageTranslationLabel}>{t('translatedMessage')}</span>
          <div className={detailsStyles.messageTranslationText}>{translatedText}</div>
        </div>
      </div>
    ) : null;

    if (knowledgeCitations.length === 0) {
      return (
        <>
          {textContent}
          {translationBlock}
        </>
      );
    }

    return (
      <>
        {textContent}
        {translationBlock}
        <div className={detailsStyles.knowledgeCitationList} aria-label={t('knowledgeCitations')}>
          <span className={detailsStyles.knowledgeCitationLabel}>{t('knowledgeCitations')}</span>
          {knowledgeCitations.map((citation, index) => (
            <a
              key={`${citation.fileName}-${citation.chunkIndex}-${index}`}
              className={detailsStyles.knowledgeCitationChip}
              href={`/ai/knowledge?search=${encodeURIComponent(citation.fileName)}&sourceFile=${encodeURIComponent(citation.fileName)}&sourceChunk=${citation.chunkIndex}&retrieveQuery=${encodeURIComponent(citation.fileName)}`}
              title={t('openKnowledgeSource')}
            >
              {t('knowledgeCitationChip', { fileName: citation.fileName, index: citation.chunkIndex })}
            </a>
          ))}
        </div>
      </>
    );
  }, [currentViewerParticipant, enableInteractiveSelection, onReplyToInteractiveSelection, onTranscribeAudio, participantOptionById, t, transcribingMessageId, translatedMessageVisibilityById, translatedMessagesById]);

  return {
    currentConversationList,
    isExternalConversation,
    isRegularDirectConversation,
    isRegularChannelConversation,
    getParticipantDisplayName,
    getConversationDisplayName,
    renderMessageContent,
    mentionDetailsPopup,
    imageViewerPopup,
  };
};